import React, { useEffect, useState } from "react";
import {
    Card, Typography, Form, Input, Select, DatePicker, Button,
    Row, Col, Space, InputNumber, Upload, message, Tag,
} from "antd";
import { ArrowLeftOutlined, InboxOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ProjectTaskService } from "../../../Services/Manage/ProjectTaskService";
import { MilestoneService } from "../../../Services/Manage/MilestoneService";
import { UserService } from "../../../Services/Manage/UserService";
import { ENDPOINT } from "../../../Services/ENDPOINT";

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

const CardTitle = ({ no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

const priorityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
];

const statusOptions = [
    { value: "To Do", label: "To Do" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
    { value: "On Hold", label: "On Hold" },
    { value: "Cancelled", label: "Cancelled" },
];

const reminderOptions = [
    { value: "1 Day Before Due Date", label: "1 Day Before Due Date" },
    { value: "2 Days Before Due Date", label: "2 Days Before Due Date" },
    { value: "3 Days Before Due Date", label: "3 Days Before Due Date" },
    { value: "1 Week Before Due Date", label: "1 Week Before Due Date" },
];

const AddTask = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();

    const [taskTypeOptions, setTaskTypeOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [dependencyOptions, setDependencyOptions] = useState([]);
    const [milestoneOptions, setMilestoneOptions] = useState([]);
    const [fileList, setFileList] = useState([]);

    const projectId = location.state?.id || null;
    const projectName = location.state?.pro_name || "-";
    const projectNo = location.state?.pro_no || "-";
    const projectDepartment = location.state?.pro_department_name || "-";

    const taskDbId = location.state?.taskDbId || null;
    const enableEdit = location.state?.enableEdit ?? true;
    const isEditMode = !!taskDbId;
    const readOnly = isEditMode && !enableEdit;
    const statusOnly = location.state?.statusOnly === true;
    const canManage = location.state?.canManage === true;

    const [taskCode, setTaskCode] = useState("TSK-001");
    const [existingRecord, setExistingRecord] = useState(null);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);

    const summary = Form.useWatch([], form) || {};

    useEffect(() => {
        if (isEditMode) {
            getTaskById(taskDbId);
        } else {
            getLastTask();
        }
    }, [taskDbId]);

    useEffect(() => {
        getAssigneeOptions();
        getDependencyOptions();
        getMilestoneOptions();
        getDropdownValues("task_type", setTaskTypeOptions);
        getDropdownValues("category", setCategoryOptions);
    }, []);

    const getDropdownValues = async (field, setter) => {
        try {
            const request = await ProjectTaskService.getDropdownValues(field);
            if (request?.status) {
                setter((request.data || []).map((v) => ({ value: v, label: v })));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getMilestoneOptions = async () => {
        try {
            const request = projectId
                ? await MilestoneService.getAllByProject(projectId)
                : await MilestoneService.getAllMilestone();
            if (request?.status) {
                setMilestoneOptions(
                    (request.data || []).map((m) => ({ value: m.milestone_code, label: m.milestone_name }))
                );
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getAssigneeOptions = async () => {
        try {
            let engineerIds = location.state?.pro_engineer;
   
            if (typeof engineerIds === "string") {
                try { engineerIds = JSON.parse(engineerIds); } catch { engineerIds = []; }
            }
            const allowedIds = new Set((engineerIds || []).filter(Boolean).map((id) => String(id).trim()));

            const request = await UserService.getAllUsers();
            const users = request?.data || [];
            const relevant = allowedIds.size
                ? users.filter((u) => allowedIds.has(String(u.user_id).trim()))
                : users;

            setAssigneeOptions(
                relevant.map((u) => ({
                    value: u.name,
                    label: `${u.name}${u.user_id ? ` (${u.user_id})` : ""}`,
                }))
            );
        } catch (error) {
            console.error(error);
        }
    };

    const getDependencyOptions = async () => {
        try {
            const request = projectId
                ? await ProjectTaskService.getAllByProject(projectId)
                : await ProjectTaskService.getAllTask();

            if (request?.status) {
                const rows = (request.data || [])
                    .filter((t) => t.id !== taskDbId)
                    .map((t) => ({
                        value: t.task_code,
                        label: `${t.task_code} - ${t.task_name}`,
                    }));
                setDependencyOptions(rows);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getLastTask = async () => {
        try {
            const request = await ProjectTaskService.getLastTask();
            let nextNo = "001";
            if (request?.status && request?.data?.task_code) {
                const seqPart = request.data.task_code.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) {
                    nextNo = String(parsed + 1).padStart(3, "0");
                }
            }
            setTaskCode(`TSK-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getTaskById = async (id) => {
        try {
            setLoadingRecord(true);
            const request = await ProjectTaskService.getTaskById(id);
            if (request?.status && request?.data) {
                const data = request.data;
                setExistingRecord(data);
                setTaskCode(data.task_code || "TSK-001");

                let attachments = data.attachments;
                if (typeof attachments === "string") {
                    try { attachments = JSON.parse(attachments || "[]"); } catch { attachments = []; }
                }
                setFileList(
                    (attachments || []).map((f, idx) => ({
                        uid: `existing-${idx}`,
                        name: f.filename || f.name,
                        status: "done",
                        url: f.path?.startsWith("http") ? f.path : `${ENDPOINT.base}/${f.path}`,
                        response: { file: f },
                    }))
                );

                form.setFieldsValue({
                    task_name: data.task_name,
                    task_type: data.task_type ? [data.task_type] : [],
                    milestone_code: data.milestone_code,
                    parent_task: data.parent_task,
                    category: data.category ? [data.category] : [],
                    description: data.description,
                    assignee: data.assignee,
                    priority: data.priority,
                    status: data.status,
                    start_date: data.start_date ? dayjs(data.start_date) : null,
                    due_date: data.due_date ? dayjs(data.due_date) : null,
                    estimated_hours: data.estimated_hours,
                    time_spent: data.time_spent,
                    acceptance_criteria: data.acceptance_criteria,
                    deliverables: data.deliverables,
                    tags: data.tags ? data.tags.split(",") : [],
                    dependency: data.dependency,
                    related_task: data.related_task,
                    reminder: data.reminder,
                    remarks: data.remarks,
                });
            } else {
                messageApi.error(request?.message || "Task not found");
            }
        } catch (error) {
            console.error("Error fetching task:", error);
            messageApi.error("Failed to load task");
        } finally {
            setLoadingRecord(false);
        }
    };

    const uploadAttachments = async () => {
        const uploadedFiles = [];
        for (const f of fileList) {
            if (!f.originFileObj) continue;
            try {
                const formData = new FormData();
                formData.append("file", f.originFileObj);
                const res = await fetch(`${ENDPOINT.ProjectTask}/upload-file`, {
                    method: "POST",
                    body: formData,
                });
                const result = await res.json();
                if (result.status && result.success) {
                    uploadedFiles.push(result.file);
                }
            } catch (e) {
                console.error("Attachment upload failed:", e);
            }
        }
        return uploadedFiles;
    };

    const buildPayload = async (values, isDraft) => {
        const user = await UserService.getOmeUser();
        const newlyUploaded = await uploadAttachments();
        const existingAttachments = fileList
            .filter((f) => !f.originFileObj)
            .map((f) => f.response?.file)
            .filter(Boolean);
        const attachments = [...existingAttachments, ...newlyUploaded];

        return {
            task_name: values.task_name,
            task_type: Array.isArray(values.task_type) ? values.task_type[0] : values.task_type,
            milestone_code: values.milestone_code,
            parent_task: values.parent_task,
            category: Array.isArray(values.category) ? values.category[0] : values.category,
            description: values.description,
            assignee: values.assignee,
            department: projectDepartment !== "-" ? projectDepartment : null,
            priority: values.priority,
            status: isDraft ? "To Do" : (values.status || "To Do"),
            start_date: values.start_date ? dayjs(values.start_date).format("YYYY-MM-DD") : null,
            due_date: values.due_date ? dayjs(values.due_date).format("YYYY-MM-DD") : null,
            estimated_hours: values.estimated_hours,
            time_spent: values.time_spent || 0,
            progress: values.progress || 0,
            acceptance_criteria: values.acceptance_criteria,
            deliverables: values.deliverables,
            tags: Array.isArray(values.tags) ? values.tags.join(",") : values.tags,
            dependency: values.dependency,
            related_task: values.related_task,
            reminder: values.reminder,
            remarks: values.remarks,
            attachments: JSON.stringify(attachments),
            task_code: taskCode,
            pro_id: projectId,
            pro_no: projectNo,
            is_draft: isDraft,
            added_by: user?.uid,
        };
    };

    const submitTask = async (values, isDraft = false) => {
        try {
            isDraft ? setSavingDraft(true) : setSubmitting(true);
            const payload = await buildPayload(values, isDraft);

            const request = isEditMode
                ? await ProjectTaskService.updateTask(payload, taskDbId)
                : await ProjectTaskService.createTask(payload);

            if (request?.status) {
                messageApi.success(
                    request.message || (isDraft ? "Saved as draft" : isEditMode ? "Task updated" : "Task created")
                );
                navigate(-1);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error submitting task:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSavingDraft(false);
            setSubmitting(false);
        }
    };

    const onFinish = (values) => {
        submitTask(values, false);
    };

    const handleSaveDraft = () => {
        const values = form.getFieldsValue();
        submitTask(values, true);
    };

    const headerLabel = isEditMode ? (readOnly ? "View Task" : "Edit Task") : "Add Task";

    return (
        <>
            <style>{compactFormStyle}</style>
            {contextHolder}

            <Card
                loading={loadingRecord}
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate(-1)}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {headerLabel}
                                    <Tag color="blue" style={{ fontSize: "16px", padding: "6px 16px", borderRadius: "8px", fontWeight: 600 }}>
                                        {taskCode}
                                    </Tag>
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    <Space>
                        {readOnly && canManage ? (
                            <Button
                                size="large"
                                onClick={() =>
                                    navigate("/overview-project/tasks/add", {
                                        state: { ...location.state, action: "Edit", enableEdit: true, statusOnly: false },
                                    })
                                }
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)", color: "#fff" }}
                            >
                                Enable Edit
                            </Button>
                        ) : readOnly && !canManage ? (
                            <Button
                                size="large"
                                onClick={() =>
                                    navigate("/overview-project/tasks/add", {
                                        state: { ...location.state, action: "Edit", enableEdit: true, statusOnly: true },
                                    })
                                }
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)", color: "#fff" }}
                            >
                                Update Status
                            </Button>
                        ) : (
                            <>
                                {!statusOnly && (
                                    <Button size="large" loading={savingDraft} onClick={handleSaveDraft}>
                                        Save as Draft
                                    </Button>
                                )}
                                <Button
                                    type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                                    style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                >
                                    {isEditMode ? "Update Task" : "Save Task"}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            disabled={readOnly}
                            initialValues={{ priority: "High", status: "To Do", progress: 0 }}
                        >
                            <Card title={<CardTitle no={1} text="Basic Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Project">
                                            <Input size="large" value={projectName} readOnly />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="task_name" label="Task Name" rules={[{ required: true, message: "Please enter task name" }]}>
                                            <Input size="large" placeholder="Enter task name" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Task Code">
                                            <Input size="large" readOnly value={taskCode} placeholder="Auto Generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="task_type" label="Task Type" rules={[{ required: true, message: "Please select task type" }]}>
                                            <Select
                                                options={taskTypeOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type task type"
                                                disabled={statusOnly}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="milestone_code" label="Milestone">
                                            <Select options={milestoneOptions} size="large" showSearch allowClear placeholder="Select milestone" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="parent_task" label="Parent Task">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Select parent task (optional)" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="category" label="Category">
                                            <Select
                                                options={categoryOptions}
                                                mode="tags"
                                                maxCount={1}
                                                size="large"
                                                showSearch
                                                allowClear
                                                placeholder="Select or type category"
                                                disabled={statusOnly}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="description" label="Description">
                                            <TextArea rows={1} size="large" maxLength={500} showCount placeholder="Enter task description" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={2} text="Assignment & Schedule" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="assignee" label="Assignee" rules={[{ required: true, message: "Please select assignee" }]}>
                                            <Select options={assigneeOptions} size="large" showSearch allowClear placeholder="Select assignee" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Department / Team">
                                            <Input size="large" value={projectDepartment} readOnly />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Please select priority" }]}>
                                            <Select options={priorityOptions} size="large" placeholder="Select priority" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="status" label="Status" rules={[{ required: true, message: "Please select status" }]}>
                                            <Select options={statusOptions} size="large" placeholder="Select status" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="start_date" label="Start Date" rules={[{ required: true, message: "Please select start date" }]}>
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="Select start date" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="due_date" label="Due Date" rules={[{ required: true, message: "Please select due date" }]}>
                                            <DatePicker size="large" style={{ width: "100%" }} format="DD-MM-YYYY" placeholder="Select due date" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="estimated_hours" label="Estimated Hours">
                                            <InputNumber size="large" min={0} style={{ width: "100%" }} placeholder="Enter estimated hours" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="time_spent" label="Time Spent">
                                            <InputNumber size="large" min={0} style={{ width: "100%" }} placeholder="0" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={3} text="Task Details" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="description" label="Detailed Description">
                                            <TextArea rows={6} size="large" maxLength={2000} showCount placeholder="Enter detailed description of the task..." disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item name="acceptance_criteria" label="Acceptance Criteria">
                                            <TextArea rows={2} size="large" maxLength={1000} showCount placeholder="Enter acceptance criteria..." disabled={statusOnly} />
                                        </Form.Item>
                                        <Form.Item name="deliverables" label="Deliverables">
                                            <TextArea rows={2} size="large" maxLength={1000} showCount placeholder="Enter deliverables..." disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle no={4} text="Additional Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="tags" label="Tags / Keywords">
                                            <Select mode="tags" size="large" open={false} suffixIcon={null} placeholder="Enter tags and press enter" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="dependency" label="Dependency">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Select dependency" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="related_task" label="Related Task (if any)">
                                            <Select options={dependencyOptions} size="large" showSearch allowClear placeholder="Search task..." disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="reminder" label="Reminder">
                                            <Select options={reminderOptions} size="large" allowClear placeholder="Select reminder" disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item label="Attachments">
                                            <Upload.Dragger
                                                fileList={fileList}
                                                multiple
                                                disabled={readOnly || statusOnly}
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                beforeUpload={(file) => {
                                                    setFileList((prev) => [...prev, { uid: `${Date.now()}-${file.name}`, name: file.name, status: "done", originFileObj: file }]);
                                                    return false;
                                                }}
                                                onRemove={(file) => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
                                                style={{ padding: "6px 0" }}
                                            >
                                                <p className="ant-upload-drag-icon" style={{ margin: "4px 0" }}><InboxOutlined /></p>
                                                <p className="ant-upload-text" style={{ fontSize: 12, margin: 0 }}>Click to upload or drag and drop</p>
                                                <p className="ant-upload-hint" style={{ fontSize: 11 }}>PNG, JPG, PDF, DOC, DOCX (Max. 10MB)</p>
                                            </Upload.Dragger>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={12}>
                                        <Form.Item name="remarks" label="Remarks">
                                            <TextArea rows={5} size="large" maxLength={500} showCount placeholder="Enter remarks (optional)..." disabled={statusOnly} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Task Summary</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Project", projectName],
                                    ["Task Name", summary.task_name || "-"],
                                    ["Task Code", taskCode],
                                    ["Task Type", Array.isArray(summary.task_type) ? summary.task_type[0] : summary.task_type || "-"],
                                    ["Assignee", summary.assignee || "-"],
                                    ["Priority", summary.priority || "-"],
                                    ["Status", summary.status || "To Do"],
                                    ["Start Date", summary.start_date ? dayjs(summary.start_date).format("DD-MM-YYYY") : "-"],
                                    ["Due Date", summary.due_date ? dayjs(summary.due_date).format("DD-MM-YYYY") : "-"],
                                    ["Estimated Hours", summary.estimated_hours || "-"],
                                    ["Time Spent", summary.time_spent || "-"],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Write a clear and specific task name.</li>
                                <li>Provide detailed description and acceptance criteria.</li>
                                <li>Set realistic estimated hours and due date.</li>
                                <li>Attach relevant files or references.</li>
                                <li>Update the status regularly for better tracking.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600 }}>Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {["View Project", "Task List", "Milestones", "Project Timeline"].map((label) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(-1)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>

                {!readOnly && (
                    <div style={{ textAlign: "right", marginTop: 16 }}>
                        <Button size="large" style={{ marginRight: 8 }} onClick={() => form.resetFields()}>
                            Reset
                        </Button>
                        <Button
                            type="primary" size="large" loading={submitting} onClick={() => form.submit()}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                        >
                            {isEditMode ? "Update Task" : "Save Task"}
                        </Button>
                    </div>
                )}
            </Card>
        </>
    );
};

export default AddTask;